import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../../api/auth-api';
import { AlertTriangle } from 'lucide-react';
import logo from '../../assets/images/logo.png';
import logoM from '../../assets/images/logoM.png';
import slideReseau from '../../assets/images/slide-reseau.png';
import slideTipQr from '../../assets/images/slide-tip-qr.png';
import slideConseil from '../../assets/images/slide-conseil.png';
import slideTipNotifs from '../../assets/images/slide-tip-notifs.png';
import slideSecurite from '../../assets/images/slide-securite.png';

const DARK = '#203114';
const MID  = '#3a6024';
const LIGHT_GREEN = '#9CCD63';

const SLIDES = [
  {
    quote: "Plus de 500 pharmacies utilisent Mazid pour recevoir leurs commissions directement sur leur téléphone.",
    label: "Réseau",
    stat: "500+",
    statLabel: "pharmacies partenaires",
    image: slideReseau,
  },
  {
    quote: "Scannez simplement le QR code d'un produit en pharmacie et recevez votre commission en quelques secondes via Damane Cash.",
    label: "Tip",
    stat: "< 10 sec",
    statLabel: "pour recevoir une commission",
    image: slideTipQr,
  },
  {
    quote: "Suivez vos ventes en temps réel depuis le tableau de bord laboratoire : région, pharmacie, vendeur — toute la transparence en un coup d'œil.",
    label: "Conseil",
    stat: "100%",
    statLabel: "de transparence sur les ventes",
    image: slideConseil,
  },
  {
    quote: "Activez les notifications Mazid pour ne jamais manquer une nouvelle promotion de vos laboratoires partenaires.",
    label: "Tip",
    stat: "3×",
    statLabel: "plus d'engagement avec les notifs",
    image: slideTipNotifs,
  },
  {
    quote: "Chaque QR code est unique et sécurisé. Notre système de détection anti-fraude protège l'intégrité de chaque transaction.",
    label: "Sécurité",
    stat: "0 fraude",
    statLabel: "grâce à la vérification en temps réel",
    image: slideSecurite,
  },
];

const Login = () => {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [slide, setSlide]         = useState(0);
  const [fading, setFading]       = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (authApi.isAuthenticated()) {
      const userType = authApi.getUserType();
      if (userType === 'ADMIN') navigate('/admin/dashboard');
      else if (userType === 'LABORATORY') navigate('/laboratory/dashboard');
    } else if (authApi.hasDeactivatedAccount()) {
      navigate('/account-deactivated');
    }
  }, [navigate]);

  // Auto-advance slides every 5s
  useEffect(() => {
    timerRef.current = setInterval(() => goTo((prev) => (prev + 1) % SLIDES.length), 10000);
    return () => clearInterval(timerRef.current);
  }, []);

  const goTo = (indexOrFn) => {
    setFading(true);
    setTimeout(() => {
      setSlide(indexOrFn);
      setFading(false);
    }, 600);
  };

  const handleDot = (i) => {
    clearInterval(timerRef.current);
    goTo(i);
    timerRef.current = setInterval(() => goTo((prev) => (prev + 1) % SLIDES.length), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await authApi.login(email, password);
      if (response) {
        const userType = response.user.userType;
        if (userType === 'ADMIN') navigate('/admin/dashboard');
        else if (userType === 'LABORATORY') navigate('/laboratory/dashboard');
        else setError('Type d\'utilisateur non autorisé sur cette plateforme');
      }
    } catch (err) {
      if (err.message === 'ACCOUNT_DEACTIVATED') { navigate('/account-deactivated'); return; }
      if (err.graphQLErrors?.length > 0) {
        const msg = err.graphQLErrors[0].message;
        if (msg.toLowerCase().includes('inactive') || msg.toLowerCase().includes('deactivated')) {
          navigate('/account-deactivated'); return;
        }
        setError(msg);
      } else if (err.networkError) {
        setError('Erreur de connexion au serveur. Veuillez réessayer.');
      } else {
        setError(err.message || 'Une erreur est survenue lors de la connexion');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const current = SLIDES[typeof slide === 'function' ? 0 : slide];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-input { transition: border-color 0.2s; }
        .login-input::placeholder { color: rgba(0,0,0,0.3); }
        .login-input:focus { outline: none; border-color: ${LIGHT_GREEN} !important; box-shadow: 0 0 0 3px rgba(156,205,99,0.15); }
        .login-btn:hover:not(:disabled) { background-color: ${MID} !important; color: #fff !important; }
        .dot { transition: all 0.3s; cursor: pointer; border: none; padding: 0; }
        .slide-content { transition: opacity 0.3s ease; }
        .right-panel { display: none; }
        @media (min-width: 768px) { .right-panel { display: flex !important; } }
      `}</style>

      <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Poppins', sans-serif" }}>

        {/* ── LEFT — Form ─────────────────────────────────────────── */}
        <div style={{
          width: '100%', maxWidth: '480px',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          padding: '48px 40px',
          backgroundColor: DARK,
          position: 'relative',
          zIndex: 1,
          boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px' }}>
            <img src={logoM} alt="Mazid" style={{ width: '88px', height: '88px', objectFit: 'contain' }} />
            <img src={logo}  alt="Mazid" style={{ width: '220px', objectFit: 'contain' }} />
          </div>

          <div style={{ width: '100%', maxWidth: '360px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#FEF2F2', marginBottom: '20px' }}>
              Connexion
            </h2>

            {error && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '12px 14px', borderRadius: '10px',
                backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
                marginBottom: '20px',
              }}>
                <AlertTriangle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{ color: '#B91C1C', fontSize: '13px', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#FEF2F2', marginBottom: '8px' }}>
                  Email ou Téléphone
                </label>
                <input
                  className="login-input"
                  type="text"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="Entrez votre email ou téléphone"
                  style={{
                    width: '100%', padding: '12px 16px',
                    border: '1.5px solid #E5E7EB', borderRadius: '10px',
                    fontSize: '15px', color: '#111827',
                    backgroundColor: '#F9FAFB',
                    boxSizing: 'border-box',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#FEF2F2', marginBottom: '8px' }}>
                  Mot de passe
                </label>
                <input
                  className="login-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="Entrez votre mot de passe"
                  style={{
                    width: '100%', padding: '12px 16px',
                    border: '1.5px solid #E5E7EB', borderRadius: '10px',
                    fontSize: '15px', color: '#111827',
                    backgroundColor: '#F9FAFB',
                    boxSizing: 'border-box',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                />
              </div>

              <div style={{ textAlign: 'right', marginBottom: '28px' }}>
                <Link to="/forgot-password" style={{ fontSize: '13px', fontWeight: '600', color: LIGHT_GREEN, textDecoration: 'none' }}>
                  Mot de passe oublié ?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="login-btn"
                style={{
                  width: '100%', padding: '13px',
                  backgroundColor: LIGHT_GREEN,
                  color: DARK, border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: '700',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  opacity: isLoading ? 0.7 : 1,
                  fontFamily: "'Poppins', sans-serif",
                  boxShadow: '0 4px 14px rgba(58,96,36,0.25)',
                }}
              >
                {isLoading ? (
                  <>
                    <svg style={{ animation: 'spin 1s linear infinite', width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="rgba(32,49,20,0.3)" strokeWidth="3" />
                      <path d="M4 12a8 8 0 018-8" stroke={DARK} strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Connexion en cours…
                  </>
                ) : 'Se connecter'}
              </button>
            </form>
          </div>

          <div style={{ position: 'absolute', bottom: '24px', color: '#D1D5DB', fontSize: '11px' }}>
            © MAZID par BOOST LINK
          </div>
        </div>

        {/* ── RIGHT — Quotes carousel ─────────────────────────────── */}
        <div
          className="right-panel"
          style={{
            flex: 1,
            background: '#FEF2F2',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 56px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background blobs */}
          <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(156,205,99,0.08)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(156,205,99,0.06)', pointerEvents: 'none' }} />

          {/* Slide content */}
          <div
            className="slide-content"
            style={{ opacity: fading ? 0 : 1, width: '100%', maxWidth: '800px', zIndex: 1 }}
          >
            {/* Label badge */}
            <span style={{
              display: 'inline-block',
              backgroundColor: DARK,
              color: LIGHT_GREEN,
              fontSize: '11px', fontWeight: '700',
              textTransform: 'uppercase', letterSpacing: '1px',
              padding: '4px 12px', borderRadius: '20px',
              marginBottom: '24px',
            }}>
              {current.label}
            </span>

            {/* Image placeholder — remplacer par une vraie image quand disponible */}
            <div style={{
              width: '100%',
              borderRadius: '16px', marginBottom: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {current.image ? (
                <img src={current.image} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '16px', display: 'block' }} />
              ) : (
                <div style={{ height: '220px', width: '100%', backgroundColor: 'rgba(255,255,255,1)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: DARK, fontSize: '13px' }}>Image à venir</span>
                </div>
              )}
            </div>

            {/* Stat */}
            <div style={{ marginBottom: '20px' }}>
              <span style={{ fontSize: '42px', fontWeight: '800', color: LIGHT_GREEN, lineHeight: '1' }}>
                {current.stat}
              </span>
              <span style={{ display: 'block', fontSize: '13px', color: DARK, marginTop: '4px' }}>
                {current.statLabel}
              </span>
            </div>

            {/* Quote */}
            <p style={{
              fontSize: '16px', color: DARK,
              lineHeight: '1.75', fontStyle: 'italic',
              borderLeft: `3px solid ${LIGHT_GREEN}`,
              paddingLeft: '16px',
              margin: 0,
            }}>
              "{current.quote}"
            </p>
          </div>

          {/* Dots navigation */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '40px', zIndex: 1 }}>
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className="dot"
                onClick={() => handleDot(i)}
                style={{
                  width: slide === i ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: slide === i ? LIGHT_GREEN : DARK,
                  background: slide === i ? LIGHT_GREEN : DARK,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
